import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Paper,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  CallMade as OutboundIcon,
  CallReceived as InboundIcon,
  SwapHoriz,
  Group as DepartmentIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import api from '../../services/api';
import errorLogger from '../../services/errorLogger.service';

const logger = errorLogger.createComponentLogger('RecordingSettings');

interface Department {
  name: string;
  enabled: boolean;
  extensions: number;
}

interface Site {
  name: string;
  enabled: boolean;
  extensions: number;
}

interface Extension {
  exten: string;
  name: string;
  enabled: boolean;
}

interface RecordingConfig {
  recordAll: boolean;
  recordIncoming: boolean;
  recordOutgoing: boolean;
  recordInternal: boolean;
  departments: Department[];
  sites: Site[];
  extensions: Extension[];
}

export default function RecordingSettings() {
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<RecordingConfig>({
    recordAll: false,
    recordIncoming: true,
    recordOutgoing: false,
    recordInternal: false,
    departments: [],
    sites: [],
    extensions: []
  });
  const [expandDepartments, setExpandDepartments] = useState(true);
  const [expandSites, setExpandSites] = useState(true);
  const [expandExtensions, setExpandExtensions] = useState(false);

  // Fetch current configuration and departments/sites
  const fetchConfig = useCallback(async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    try {
      setLoading(true);
      setError(null);

      // Get customer ID - check login as mode first
      let customerId: string | number | undefined;
      if (isLoginAsCustomer && member?.customerID) {
        logger.debug('LOGIN AS MODE - Using customerID:', member.customerID);
        customerId = member.customerID;
      } else if (customerFilter.isFiltered && customerFilter.customerId) {
        logger.debug('FILTER MODE - Using customerFilter:', customerFilter.customerId);
        customerId = customerFilter.customerId;
      } else {
        logger.debug('NO FILTER - Will use JWT customer');
        customerId = member?.customerID;
      }

      if (!customerId) {
        setError('No customer selected');
        setLoading(false);
        return;
      }

      let extensionsArray: any[] = [];
      let departmentsData: any[] = [];
      let sitesData: any[] = [];

      try {
        // Fetch extensions, departments, and sites
        const extResponse = await api.get('/endpoints/recording-extensions', {
          params: { customerId }
        });

        if (extResponse.data.success) {
          extensionsArray = extResponse.data.data?.extensions || [];
          departmentsData = extResponse.data.data?.departments || [];
          sitesData = extResponse.data.data?.sites || [];
        }
      } catch (extError: any) {
        // Continue without extensions data
      }

      // Initialize settings
      let recordAll = false;
      let recordIncoming = true;
      let recordOutgoing = false;
      let recordInternal = false;
      const deptSettings = new Map<string, boolean>();
      const siteSettings = new Map<string, boolean>();
      const extSettings = new Map<string, boolean>();

      try {
        // Fetch current recording rules
        const rulesResponse = await api.get('/endpoints/recording-rules', {
          params: { customerId }
        });

        if (rulesResponse.data.success && rulesResponse.data.rules.length > 0) {
          // Parse existing rules
          rulesResponse.data.rules.forEach((rule: any) => {
            if (rule.rule_type === 'global' && rule.is_active) {
              recordAll = rule.action === 'record';
            } else if (rule.rule_type === 'direction' && rule.is_active) {
              if (rule.rule_value === 'inbound') {
                recordIncoming = rule.action === 'record';
              } else if (rule.rule_value === 'outbound') {
                recordOutgoing = rule.action === 'record';
              } else if (rule.rule_value === 'internal') {
                recordInternal = rule.action === 'record';
              }
            } else if (rule.rule_type === 'department' && rule.is_active) {
              deptSettings.set(rule.rule_value, rule.action === 'record');
            } else if (rule.rule_type === 'site' && rule.is_active) {
              siteSettings.set(rule.rule_value, rule.action === 'record');
            } else if (rule.rule_type === 'extension' && rule.is_active) {
              extSettings.set(rule.rule_value, rule.action === 'record');
            }
          });
        }
      } catch (rulesError) {
        // Continue with defaults - rules endpoint error is not critical
      }

      // Build config - always set this
      const newConfig = {
        recordAll,
        recordIncoming,
        recordOutgoing,
        recordInternal,
        departments: departmentsData.map((dept: any) => ({
          name: dept.department,
          enabled: deptSettings.get(dept.department) ?? recordAll,
          extensions: parseInt(dept.count)
        })),
        sites: sitesData.map((site: any) => ({
          name: site.site,
          enabled: siteSettings.get(site.site) ?? recordAll,
          extensions: parseInt(site.count)
        })),
        extensions: extensionsArray.map((ext: any) => ({
          exten: ext.extension,
          name: ext.name,
          enabled: extSettings.get(ext.extension) ?? recordAll
        }))
      };
      
      setConfig(newConfig);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading configuration');
    } finally {
      setLoading(false);
    }
  }, [customerFilter.isFiltered, customerFilter.customerId, member?.customerID, isLoginAsCustomer, waitingForLoginAs]);

  useEffect(() => {
    if (!member) return;
    
    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }
    
    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchConfig();
    }, 200);
    
    return () => clearTimeout(timer);
  }, [fetchConfig, member?.customerID, isLoginAsCustomer]);

  // Listen for login-as-customer changes to update tab title and refetch data
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Recording Settings`;
        // Also refetch data when login-as changes
        setTimeout(() => {
          if (member) {
            fetchConfig();
          }
        }, 100);
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Recording Settings`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);

  // Save configuration to server
  const saveConfigToServer = async (configToSave: RecordingConfig) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Get customer ID - check login as mode first
      let customerId: string | number | undefined;
      if (isLoginAsCustomer && member?.customerID) {
        customerId = member.customerID;
      } else if (customerFilter.isFiltered && customerFilter.customerId) {
        customerId = customerFilter.customerId;
      } else {
        customerId = member?.customerID;
      }

      if (!customerId) {
        setError('No customer selected');
        return;
      }

      // First, delete all existing rules for this customer
      try {
        await api.delete(`/endpoints/recording-rules/bulk/${customerId}`);
      } catch (deleteError) {
        // If delete fails, continue anyway - might be no rules to delete
      }

      // Create new rules based on switches
      const rules = [];

      // Global rule
      rules.push({
        custid: customerId,
        rule_name: 'Global Recording',
        rule_type: 'global',
        rule_value: '',
        action: configToSave.recordAll ? 'record' : 'no_record',
        priority: 1,
        is_active: 1
      });

      // Only add direction rules if record all is off
      if (!configToSave.recordAll) {
        // Direction rules
        rules.push({
          custid: customerId,
          rule_name: 'Incoming Calls',
          rule_type: 'direction',
          rule_value: 'inbound',
          action: configToSave.recordIncoming ? 'record' : 'no_record',
          priority: 10,
          is_active: 1
        });

        rules.push({
          custid: customerId,
          rule_name: 'Outgoing Calls',
          rule_type: 'direction',
          rule_value: 'outbound',
          action: configToSave.recordOutgoing ? 'record' : 'no_record',
          priority: 20,
          is_active: 1
        });

        rules.push({
          custid: customerId,
          rule_name: 'Internal Calls',
          rule_type: 'direction',
          rule_value: 'internal',
          action: configToSave.recordInternal ? 'record' : 'no_record',
          priority: 25,
          is_active: 1
        });

        // Department rules (only if different from global)
        let priority = 30;
        configToSave.departments.forEach(dept => {
          const shouldRecord = dept.enabled;
          const globalWouldRecord = configToSave.recordIncoming || configToSave.recordOutgoing;
          
          if (shouldRecord !== globalWouldRecord) {
            rules.push({
              custid: customerId,
              rule_name: `Department: ${dept.name}`,
              rule_type: 'department',
              rule_value: dept.name,
              action: dept.enabled ? 'record' : 'no_record',
              priority: priority++,
              is_active: 1
            });
          }
        });

        // Site rules (only if different from global)
        configToSave.sites.forEach(site => {
          const shouldRecord = site.enabled;
          const globalWouldRecord = configToSave.recordIncoming || configToSave.recordOutgoing || configToSave.recordInternal;
          
          if (shouldRecord !== globalWouldRecord) {
            rules.push({
              custid: customerId,
              rule_name: `Site: ${site.name}`,
              rule_type: 'site',
              rule_value: site.name,
              action: site.enabled ? 'record' : 'no_record',
              priority: priority++,
              is_active: 1
            });
          }
        });

        // Extension rules (only if different from global)
        configToSave.extensions.forEach(ext => {
          const shouldRecord = ext.enabled;
          const globalWouldRecord = configToSave.recordIncoming || configToSave.recordOutgoing || configToSave.recordInternal;
          
          if (shouldRecord !== globalWouldRecord) {
            rules.push({
              custid: customerId,
              rule_name: `Extension: ${ext.exten} - ${ext.name}`,
              rule_type: 'extension',
              rule_value: ext.exten,
              action: ext.enabled ? 'record' : 'no_record',
              priority: priority++,
              is_active: 1
            });
          }
        });
      }

      // Create all rules
      const params = { customerId };
      for (const rule of rules) {
        await api.post('/endpoints/recording-rules', rule, { params });
      }

      setSuccess('Recording settings saved successfully');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving configuration');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAll = async (checked: boolean) => {
    const newConfig = {
      ...config,
      recordAll: checked,
      // If turning on record all, enable all departments/sites/extensions
      departments: config.departments.map(d => ({ ...d, enabled: checked })),
      sites: config.sites.map(s => ({ ...s, enabled: checked })),
      extensions: config.extensions.map(e => ({ ...e, enabled: checked }))
    };
    setConfig(newConfig);
    await saveConfigToServer(newConfig);
  };

  const handleToggleDirection = async (direction: 'incoming' | 'outgoing' | 'internal', checked: boolean) => {
    const newConfig = {
      ...config,
      recordIncoming: direction === 'incoming' ? checked : config.recordIncoming,
      recordOutgoing: direction === 'outgoing' ? checked : config.recordOutgoing,
      recordInternal: direction === 'internal' ? checked : config.recordInternal
    };
    setConfig(newConfig);
    await saveConfigToServer(newConfig);
  };

  const handleToggleDepartment = async (name: string, checked: boolean) => {
    const newConfig = {
      ...config,
      departments: config.departments.map(d => 
        d.name === name ? { ...d, enabled: checked } : d
      )
    };
    setConfig(newConfig);
    await saveConfigToServer(newConfig);
  };

  const handleToggleSite = async (name: string, checked: boolean) => {
    const newConfig = {
      ...config,
      sites: config.sites.map(s => 
        s.name === name ? { ...s, enabled: checked } : s
      )
    };
    setConfig(newConfig);
    await saveConfigToServer(newConfig);
  };

  const handleToggleExtension = async (exten: string, checked: boolean) => {
    const newConfig = {
      ...config,
      extensions: config.extensions.map(e => 
        e.exten === exten ? { ...e, enabled: checked } : e
      )
    };
    setConfig(newConfig);
    await saveConfigToServer(newConfig);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Call Recording Settings
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Master Switch */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6">Record All Calls</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Enable recording for all calls regardless of direction or department
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.recordAll}
                      onChange={(e) => handleToggleAll(e.target.checked)}
                      disabled={saving}
                      size="medium"
                    />
                  }
                  label={config.recordAll ? "ON" : "OFF"}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Direction Controls - Only show if Record All is OFF */}
        <Grid item xs={12}>
          <Collapse in={!config.recordAll} timeout="auto" unmountOnExit>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recording by Direction
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                        <Box display="flex" alignItems="center" flex={1}>
                          <InboundIcon sx={{ mr: 1, color: 'info.main' }} />
                          <Box>
                            <Typography variant="subtitle1">Incoming Calls</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Record calls from external numbers
                            </Typography>
                          </Box>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.recordIncoming}
                              onChange={(e) => handleToggleDirection('incoming', e.target.checked)}
                              disabled={saving}
                            />
                          }
                          label=""
                        />
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                        <Box display="flex" alignItems="center" flex={1}>
                          <OutboundIcon sx={{ mr: 1, color: 'warning.main' }} />
                          <Box>
                            <Typography variant="subtitle1">Outgoing Calls</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Record calls to external numbers
                            </Typography>
                          </Box>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.recordOutgoing}
                              onChange={(e) => handleToggleDirection('outgoing', e.target.checked)}
                              disabled={saving}
                            />
                          }
                          label=""
                        />
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                        <Box display="flex" alignItems="center" flex={1}>
                          <SwapHoriz sx={{ mr: 1, color: 'success.main' }} />
                          <Box>
                            <Typography variant="subtitle1">Internal Calls</Typography>
                            <Typography variant="caption" color="textSecondary">
                              Record extension to extension
                            </Typography>
                          </Box>
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.recordInternal}
                              onChange={(e) => handleToggleDirection('internal', e.target.checked)}
                              disabled={saving}
                            />
                          }
                          label=""
                        />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Collapse>
        </Grid>

        {/* Department Controls */}
        <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <DepartmentIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Departments</Typography>
                    <Chip 
                      label={`${config.departments.length} total`} 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Box>
                  <IconButton onClick={() => setExpandDepartments(!expandDepartments)}>
                    {expandDepartments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandDepartments}>
                  {config.departments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                      No departments configured. Add departments in the Extensions page.
                    </Typography>
                  ) : (
                  <List>
                    {config.departments.map((dept) => (
                      <ListItem key={dept.name} divider>
                        <ListItemText
                          primary={dept.name}
                          secondary={`${dept.extensions} extension${dept.extensions !== 1 ? 's' : ''}`}
                        />
                        <ListItemSecondaryAction>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config.recordAll || dept.enabled}
                                onChange={(e) => handleToggleDepartment(dept.name, e.target.checked)}
                                disabled={saving || config.recordAll}
                              />
                            }
                            label=""
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  )}
                  {config.recordAll && (
                    <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                      Department settings are overridden when "Record All Calls" is enabled
                    </Alert>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

        {/* Site Controls */}
        <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <LocationIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Sites</Typography>
                    <Chip 
                      label={`${config.sites.length} total`} 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Box>
                  <IconButton onClick={() => setExpandSites(!expandSites)}>
                    {expandSites ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandSites}>
                  {config.sites.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                      No sites configured. Add sites in the Extensions page.
                    </Typography>
                  ) : (
                  <List>
                    {config.sites.map((site) => (
                      <ListItem key={site.name} divider>
                        <ListItemText
                          primary={site.name}
                          secondary={`${site.extensions} extension${site.extensions !== 1 ? 's' : ''}`}
                        />
                        <ListItemSecondaryAction>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config.recordAll || site.enabled}
                                onChange={(e) => handleToggleSite(site.name, e.target.checked)}
                                disabled={saving || config.recordAll}
                              />
                            }
                            label=""
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  )}
                  {config.recordAll && (
                    <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                      Site settings are overridden when "Record All Calls" is enabled
                    </Alert>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

        {/* Extension Controls */}
        <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Extensions</Typography>
                    <Chip 
                      label={`${config.extensions.length} total`} 
                      size="small" 
                      sx={{ ml: 2 }} 
                    />
                  </Box>
                  <IconButton onClick={() => setExpandExtensions(!expandExtensions)}>
                    {expandExtensions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                
                <Collapse in={expandExtensions}>
                  {config.extensions.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                      No extensions configured. Add extensions first.
                    </Typography>
                  ) : (
                  <List>
                    {config.extensions.map((ext) => (
                      <ListItem key={ext.exten} divider>
                        <ListItemText
                          primary={`${ext.exten} - ${ext.name}`}
                        />
                        <ListItemSecondaryAction>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config.recordAll || ext.enabled}
                                onChange={(e) => handleToggleExtension(ext.exten, e.target.checked)}
                                disabled={saving || config.recordAll}
                              />
                            }
                            label=""
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  )}
                  {config.recordAll && (
                    <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                      Extension settings are overridden when "Record All Calls" is enabled
                    </Alert>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>

      </Grid>
    </Box>
  );
}